import { useState } from "react";

interface ChipOption {
  key: string;
  label: string;
}

/** Chip selection state + combining selected labels with free text into one instruction. */
export function useAiSteering(options: ChipOption[]) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const reset = () => setSelected([]);
  const combine = (freeText: string) =>
    [...options.filter((o) => selected.includes(o.key)).map((o) => o.label), freeText.trim()]
      .filter(Boolean)
      .join(", ");
  return { selected, toggle, reset, combine };
}

interface Props {
  options: ChipOption[];
  selected: string[];
  onToggle: (key: string) => void;
  disabled?: boolean;
}

/**
 * One-tap steering shortcuts for the AI improve steps ("Warmer", "Fix
 * mistakes", …). Toggled chips are combined with any free-text instruction.
 */
const AiSteerChips = ({ options, selected, onToggle, disabled }: Props) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((option) => {
      const active = selected.includes(option.key);
      return (
        <button
          key={option.key}
          type="button"
          aria-pressed={active}
          disabled={disabled}
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            active
              ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
              : "with-border text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
          }`}
          onClick={() => onToggle(option.key)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export default AiSteerChips;
